import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useCatalogoApi } from '../catalogo/api'
import { t, Lang } from './i18n'
import { localeFor } from '../utils/format'
import dialogStyles from '../styles/Dialog.module.css'
import styles from './PanelDetailDrawer.module.css'

interface SesionPendiente {
  fecha: string
  tipo: string
}

interface JornadaPendiente {
  numero: number
  fecha: string | null
  rival: string | null
}

interface EquipoEstadoDetalle {
  sesiones_pendientes: SesionPendiente[]
  jornadas_pendientes: JornadaPendiente[]
}

const DIA_MS = 24 * 60 * 60 * 1000

function diasDesde(fecha: string, hoy: Date): number {
  const inicioHoy = Date.UTC(hoy.getUTCFullYear(), hoy.getUTCMonth(), hoy.getUTCDate())
  return Math.max(0, Math.round((inicioHoy - new Date(fecha).getTime()) / DIA_MS))
}

function antiguedad(dias: number, lang: Lang): string {
  if (dias === 0) return t('hoy', lang)
  if (dias === 1) return t('ayer', lang)
  return t('haceDias', lang).replace('{n}', String(dias))
}

// Cuanto más tiempo lleva sin pasarse lista, más llamativo el borde de la tarjeta.
function nivelAntiguedad(dias: number): 'reciente' | 'atrasada' | 'muyAtrasada' {
  if (dias <= 2) return 'reciente'
  if (dias <= 7) return 'atrasada'
  return 'muyAtrasada'
}

// Fecha como hoja de calendario: día de la semana, número grande y mes.
function HojaCalendario({ fecha, locale }: { fecha: string; locale: string }) {
  const d = new Date(fecha)
  const fmt = (o: Intl.DateTimeFormatOptions) => new Intl.DateTimeFormat(locale, { ...o, timeZone: 'UTC' }).format(d)
  return (
    <span className={styles.hoja} aria-hidden>
      <span className={styles.hojaSemana}>{fmt({ weekday: 'short' })}</span>
      <span className={styles.hojaDia}>{d.getUTCDate()}</span>
      <span className={styles.hojaMes}>{fmt({ month: 'short' })}</span>
    </span>
  )
}

// Look & feel (2026-10-22): "en ALERTA, cuando salen pendientes, es un listado plano". Antes era una
// lista «17/10/2026 — entrenamiento»; ahora cada sesión/jornada pendiente es una tarjeta con la fecha
// como hoja de calendario, tipo, antigüedad (más roja cuanto más atrasada) y un acceso al equipo.
export function PanelDetailDrawer({
  equipoId,
  equipoNombre,
  lang,
  onClose,
  onRecordatorio,
}: {
  equipoId: string
  equipoNombre: string
  lang: Lang
  onClose: () => void
  // Issue #78: enviar el recordatorio de asistencia pendiente desde aquí también.
  onRecordatorio?: () => void
}) {
  const api = useCatalogoApi()
  const navigate = useNavigate()
  const [detalle, setDetalle] = useState<EquipoEstadoDetalle | null>(null)

  useEffect(() => {
    api.get(`/panel/estado/${equipoId}`).then(setDetalle)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [equipoId])

  if (!detalle) return null

  const sinPendientes = detalle.sesiones_pendientes.length === 0 && detalle.jornadas_pendientes.length === 0
  const locale = localeFor(lang)
  const hoy = new Date()
  const sesiones = [...detalle.sesiones_pendientes].sort((a, b) => a.fecha.localeCompare(b.fecha))
  const nSes = sesiones.length
  const nJor = detalle.jornadas_pendientes.length

  return (
    <dialog open onClose={onClose} className={dialogStyles.dialog}>
      <h3>
        {t('detallePendientesTitulo', lang)} — {equipoNombre}
      </h3>
      {sinPendientes ? (
        <p className={styles.alDia}>
          <span aria-hidden>✅ </span>
          <span>{t('todoAlDia', lang)}</span>
        </p>
      ) : (
        <>
          {nSes > 0 && (
            <section className={styles.seccion}>
              <h4 className={styles.seccionTitulo}>
                {t('sesionesPendientes', lang)}
                <span className={styles.contador}>
                  {nSes} {nSes === 1 ? t('sesion', lang) : t('sesionesN', lang)}
                </span>
              </h4>
              <ul className={styles.tarjetas}>
                {sesiones.map((sesion, i) => {
                  const dias = diasDesde(sesion.fecha, hoy)
                  const esPartido = sesion.tipo === 'partido'
                  return (
                    <li key={i} className={`${styles.tarjeta} ${styles[nivelAntiguedad(dias)]}`}>
                      <HojaCalendario fecha={sesion.fecha} locale={locale} />
                      <span className={styles.tarjetaTexto}>
                        <span>
                          {esPartido ? '⚽' : '🏃'} {esPartido ? t('partido', lang) : t('entrenamiento', lang)}
                        </span>
                        <span className={styles.antiguedad}>{antiguedad(dias, lang)}</span>
                      </span>
                    </li>
                  )
                })}
              </ul>
            </section>
          )}
          {nJor > 0 && (
            <section className={styles.seccion}>
              <h4 className={styles.seccionTitulo}>
                {t('jornadasPendientes', lang)}
                <span className={styles.contador}>
                  {nJor} {nJor === 1 ? t('jornadaN', lang) : t('jornadasN', lang)}
                </span>
              </h4>
              <ul className={styles.tarjetas}>
                {detalle.jornadas_pendientes.map((jornada) => {
                  const dias = jornada.fecha ? diasDesde(jornada.fecha, hoy) : null
                  return (
                    <li key={jornada.numero} className={`${styles.tarjeta} ${dias !== null ? styles[nivelAntiguedad(dias)] : ''}`}>
                      {jornada.fecha ? (
                        <HojaCalendario fecha={jornada.fecha} locale={locale} />
                      ) : (
                        <span className={styles.hoja} aria-hidden>
                          <span className={styles.hojaDia}>J{jornada.numero}</span>
                        </span>
                      )}
                      <span className={styles.tarjetaTexto}>
                        <span>
                          ⚽ <strong>J{jornada.numero}</strong>
                          {jornada.rival ? ` · ${jornada.rival}` : ''}
                        </span>
                        {dias !== null && <span className={styles.antiguedad}>{antiguedad(dias, lang)}</span>}
                      </span>
                    </li>
                  )
                })}
              </ul>
            </section>
          )}
        </>
      )}
      <div className="modal-actions">
        {onRecordatorio && nSes > 0 && <button onClick={onRecordatorio}>{t('enviarRecordatorio', lang)}</button>}
        {!sinPendientes && (
          <button data-variant="primary" onClick={() => navigate(`/equipos/${equipoId}`)}>
            {t('irAlEquipo', lang)} →
          </button>
        )}
        <button onClick={onClose}>{t('cerrar', lang)}</button>
      </div>
    </dialog>
  )
}
